import React, { useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WizardContainer from '../../components/Wizard/WizardContainer';
import { ApplicationStatus } from '../../types/wizard';
import { usePageTracking, useWizardStepTracking, useStepTimer, useAnalytics } from '../../hooks/useAnalytics';
import { courierApiUrl } from '../../config';
import {
  confirmDocumentUpload,
  getApplicantV2ErrorMessage,
  loadApplicantV2Session,
  requestDocumentUpload,
  submitApplicantV2,
} from '../../utils/applicantV2';

interface UploadDocumentsProps {
  applicationStatus: ApplicationStatus;
}

type DocumentKey = 'drivers-license' | 'insurance';
type UploadStatus = 'ready' | 'uploading' | 'submitting' | 'success' | 'error';

const requiredDocuments: Array<{ id: DocumentKey; label: string; description: string }> = [
  {
    id: 'drivers-license',
    label: "Driver's Licence",
    description: "Upload a clear photo or PDF of your driver's licence.",
  },
  {
    id: 'insurance',
    label: 'Proof of Auto Insurance',
    description: "Upload proof that you're covered for commercial food delivery.",
  },
];

const validFileTypes = ['image/jpeg', 'image/png', 'application/pdf'];

const UploadDocuments: React.FC<UploadDocumentsProps> = ({ applicationStatus }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || searchParams.get('email') || '';
  const isV2Application = Boolean(applicationStatus.applicationId);

  const [status, setStatus] = useState<UploadStatus>('ready');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<Partial<Record<DocumentKey, File>>>({});
  const [uploadedDocuments, setUploadedDocuments] = useState<Partial<Record<DocumentKey, boolean>>>({
    'drivers-license': applicationStatus.driversLicenseStatus === 'SUBMITTED',
    insurance: applicationStatus.insuranceStatus === 'SUBMITTED',
  });
  const [uploadProgress, setUploadProgress] = useState<Partial<Record<DocumentKey, number>>>({});
  const [activeDocument, setActiveDocument] = useState<DocumentKey | null>(null);

  usePageTracking('Upload Documents');
  useWizardStepTracking('upload-documents', 4, 7);
  const { getTimeSpent } = useStepTimer();
  const { trackDocumentUploadStart, trackDocumentUploadComplete, trackDocumentUploadError, trackDocumentValidationError, trackWizardStepComplete } = useAnalytics();
  const uploadStartTime = useRef<number>(0);

  const validateFile = (documentType: DocumentKey, file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      trackDocumentValidationError(documentType, 'File size must be less than 10MB');
      return 'File size must be less than 10MB';
    }

    if (!validFileTypes.includes(file.type)) {
      trackDocumentValidationError(documentType, 'Invalid file type');
      return 'Please upload a PDF, JPG, or PNG file';
    }

    return '';
  };

  const handleFileSelect = (documentType: DocumentKey, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(documentType, file);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSelectedFiles((current) => ({ ...current, [documentType]: file }));
    setUploadedDocuments((current) => ({ ...current, [documentType]: false }));
    setErrorMessage('');
  };

  const handleUpload = async (documentType: DocumentKey) => {
    const selectedFile = selectedFiles[documentType];
    const v2Session = loadApplicantV2Session();
    if (!selectedFile || (!v2Session && !token)) return;

    trackDocumentUploadStart(documentType);
    uploadStartTime.current = Date.now();
    setStatus('uploading');
    setActiveDocument(documentType);
    setUploadProgress((current) => ({ ...current, [documentType]: 0 }));
    setErrorMessage('');

    try {
      const upload = v2Session
        ? await requestDocumentUpload(v2Session, documentType, selectedFile)
        : (await axios.get(courierApiUrl('/documents/generate-presigned-url'), {
            params: { token, documentType, fileName: selectedFile.name },
          })).data;
      const presignedUrl = upload.uploadUrl || upload.presignedUrl;
      const documentKey = upload.documentKey || upload.s3Key;

      await axios.put(presignedUrl, selectedFile, {
        headers: {
          'Content-Type': selectedFile.type,
        },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress((current) => ({ ...current, [documentType]: progress }));
        },
      });

      if (v2Session) {
        await confirmDocumentUpload(v2Session, documentType, documentKey);
      } else {
        await axios.post(courierApiUrl('/documents/confirm-upload'), {
          token,
          s3Key: documentKey,
          documentType,
        });
      }

      setUploadedDocuments((current) => ({ ...current, [documentType]: true }));
      setUploadProgress((current) => ({ ...current, [documentType]: 100 }));
      setStatus('ready');
      setActiveDocument(null);
      trackDocumentUploadComplete({
        documentType,
        fileSize: selectedFile.size,
        uploadDuration: Math.floor((Date.now() - uploadStartTime.current) / 1000),
      });
    } catch (error) {
      console.error('Upload error:', error);

      if (axios.isAxiosError(error) && error.response?.status === 403 && error.response?.data?.alreadyUploaded) {
        setUploadedDocuments((current) => ({ ...current, [documentType]: true }));
        setUploadProgress((current) => ({ ...current, [documentType]: 100 }));
        setStatus('ready');
        setActiveDocument(null);
        return;
      }

      setStatus('error');
      setActiveDocument(null);
      trackDocumentUploadError({
        documentType,
        errorType: 'upload_failed',
        errorMessage: axios.isAxiosError(error) ? error.response?.data?.error || 'Upload failed' : 'Unexpected error',
      });
      setErrorMessage(getApplicantV2ErrorMessage(error, 'Failed to upload document. Please try again.'));
    }
  };

  const handleSubmitDocuments = async () => {
    const v2Session = loadApplicantV2Session();
    if ((!v2Session && !token) || !requiredDocuments.every((document) => uploadedDocuments[document.id])) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = v2Session
        ? await submitApplicantV2(v2Session)
        : await axios.post(courierApiUrl('/documents/submit-application'), {
            token,
            agreementConfirmed: true,
          });

      setStatus('success');
      trackWizardStepComplete({ stepName: 'upload-documents', timeOnStep: getTimeSpent(), completionStatus: 'success' });

      setTimeout(() => {
        navigate(response.data.applicationStatus === 'NOT_APPROVED'
          ? '/wizard/approved'
          : `/wizard/document-review${v2Session ? '' : `?token=${token}`}`);
      }, 2000);
    } catch (error) {
      console.error('Submit documents error:', error);
      setStatus('error');
      setErrorMessage(getApplicantV2ErrorMessage(error, 'Failed to submit documents. Please try again.'));
    }
  };

  const allDocumentsUploaded = requiredDocuments.every((document) => uploadedDocuments[document.id]);

  return (
    <WizardContainer
      currentStepId="upload-documents"
      applicationStatus={applicationStatus}
    >
      <div className="max-w-2xl mx-auto">
        <div className="card p-8">
          <h1 className="text-2xl font-bold text-charcoal mb-2">Upload Your Documents</h1>
          <p className="text-gray-600 mb-6">
            Please upload your driver's licence and proof of auto insurance. Our operations team will review your completed application.
          </p>

          <div className="bg-logo-green/10 border border-logo-green rounded-lg p-4 mb-6">
            <p className="font-semibold text-charcoal mb-1">Required Documents</p>
            <p className="text-sm text-gray-600">
              Accepted formats: PDF, JPG, PNG (max 10MB each).
            </p>
          </div>

          {status !== 'success' && (
            <>
              <div className="space-y-5 mb-6">
                {requiredDocuments.map((document) => {
                  const selectedFile = selectedFiles[document.id];
                  const isUploaded = uploadedDocuments[document.id];
                  const isUploading = status === 'uploading' && activeDocument === document.id;

                  return (
                    <div key={document.id} className="selection-card p-5">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <h2 className="text-lg font-semibold text-charcoal">{document.label}</h2>
                          <p className="text-sm text-gray-600">{document.description}</p>
                        </div>
                        {isUploaded && (
                          <span className="text-sm font-semibold text-logo-green">Uploaded</span>
                        )}
                      </div>

                      {(!isUploaded || !isV2Application) && (
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(event) => handleFileSelect(document.id, event)}
                          disabled={isUploading || status === 'submitting'}
                          className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            file:bg-logo-green file:text-white
                            hover:file:bg-shadow-green
                            file:cursor-pointer cursor-pointer"
                        />
                      )}

                      {selectedFile && (
                        <div className="soft-panel my-3 p-3">
                          <p className="text-sm text-gray-600">Selected file:</p>
                          <p className="font-semibold text-charcoal">{selectedFile.name}</p>
                          <p className="text-sm text-gray-500">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      )}

                      {isUploading && (
                        <div className="my-3">
                          <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium text-charcoal">Uploading...</span>
                            <span className="text-sm font-medium text-charcoal">{uploadProgress[document.id] || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className="bg-logo-green h-2.5 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress[document.id] || 0}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {(!isUploaded || !isV2Application) && (
                        <button
                          onClick={() => handleUpload(document.id)}
                          disabled={!selectedFile || isUploading || status === 'submitting'}
                          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUploaded ? 'Replace Uploaded File' : `Upload ${document.label}`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {errorMessage && (
                <div className="bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4 mb-6">
                  <p>{errorMessage}</p>
                </div>
              )}

              <button
                onClick={handleSubmitDocuments}
                disabled={!allDocumentsUploaded || status === 'uploading' || status === 'submitting'}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Submitting...' : 'Submit Documents for Review'}
              </button>
            </>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-logo-green/10 rounded-full mb-4">
                <svg className="w-8 h-8 text-logo-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Documents Submitted!</h2>
              <p className="text-gray-600 mb-4">
                Your documents have been submitted for review.
              </p>
              <p className="text-sm text-gray-500">
                We'll contact you after our operations team reviews your application.
              </p>
            </div>
          )}
        </div>
      </div>
    </WizardContainer>
  );
};

export default UploadDocuments;
